import React, { useMemo, useState } from "react";
import { Button, FilterChips, Input, Modal, SlideOver, Text } from "@teals/ui-web";
import {
  useCreateMcpIntegrationMutation,
  useVerifyMcpIntegrationMutation,
  type CallerTokenIn,
  type CreateMcpIntegrationRequest,
  type CredentialTemplate,
  type McpAuthMode,
  type McpIntegration,
  type VerifyMcpIntegrationRequest,
} from "../../store/api/mcpIntegrationsApi";
import { WizardFooter, FooterActions, ErrorBanner } from "../DataSources/styles";
import StepIndicator from "../DataSources/CreateWizard/StepIndicator";

interface CustomMcpWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenDetail: (mcp: McpIntegration) => void;
}

const STEP_LABELS: Record<number, string> = {
  1: "Step 1 of 5 — Name",
  2: "Step 2 of 5 — Server + auth",
  3: "Step 3 of 5 — Auth parameters",
  4: "Step 4 of 5 — Try connection",
  5: "Step 5 of 5 — Created",
};

function unwrapError(e: unknown): string {
  if (typeof e === "object" && e !== null && "data" in e) {
    return String((e as { data: { message?: string } }).data?.message ?? e);
  }
  return "Request failed";
}

function snippet(location: CallerTokenIn, param: string): string {
  if (location === "header") {
    return `POST /v1/chat\nx-api-key: lf_ak_…\nx-secret-key: lf_sk_…\n${param}: <user-scoped token>`;
  }
  return `{\n  "parameters": { "${param}": "<user-scoped token>" }\n}`;
}

const CustomMcpWizard: React.FC<CustomMcpWizardProps> = ({
  isOpen,
  onClose,
  onOpenDetail,
}) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [serverUrl, setServerUrl] = useState("");
  const [authMode, setAuthMode] = useState<McpAuthMode>("caller_token");
  const [callerTokenIn, setCallerTokenIn] = useState<CallerTokenIn>("header");
  const [payloadParameter, setPayloadParameter] = useState("x-cv-mcp-token");
  const [credentialTemplate, setCredentialTemplate] =
    useState<CredentialTemplate>("key_pair");
  const [trialToken, setTrialToken] = useState("");
  const [accessKey, setAccessKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [singleToken, setSingleToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<McpIntegration | null>(null);
  const [skipConfirm, setSkipConfirm] = useState(false);
  const [verifiedOk, setVerifiedOk] = useState(false);

  const [createMcp, { isLoading: creating }] = useCreateMcpIntegrationMutation();
  const [verifyMcp, { isLoading: verifying }] = useVerifyMcpIntegrationMutation();

  const busy = creating || verifying;

  const reset = () => {
    setStep(1);
    setName("");
    setDescription("");
    setServerUrl("");
    setAuthMode("caller_token");
    setCallerTokenIn("header");
    setPayloadParameter("x-cv-mcp-token");
    setCredentialTemplate("key_pair");
    setTrialToken("");
    setAccessKey("");
    setSecretKey("");
    setSingleToken("");
    setError(null);
    setCreated(null);
    setSkipConfirm(false);
    setVerifiedOk(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const buildPayload = (): CreateMcpIntegrationRequest => {
    const payload: CreateMcpIntegrationRequest = {
      name: name.trim(),
      description: description.trim() || undefined,
      server_url: serverUrl.trim(),
      auth_mode: authMode,
    };
    if (authMode === "caller_token") {
      payload.caller_token_in = callerTokenIn;
      payload.payload_parameter = payloadParameter.trim();
    } else {
      payload.credential_template = credentialTemplate;
      if (credentialTemplate === "key_pair") {
        payload.credentials = {
          access_key: accessKey,
          secret_key: secretKey,
        };
      } else if (singleToken) {
        payload.credentials = { token: singleToken };
      }
    }
    return payload;
  };

  const ensureCreated = async (): Promise<McpIntegration> => {
    if (created) return created;
    const row = await createMcp(buildPayload()).unwrap();
    setCreated(row);
    return row;
  };

  const handleTry = async () => {
    setError(null);
    try {
      const row = await ensureCreated();
      const body: VerifyMcpIntegrationRequest =
        authMode === "caller_token"
          ? { trial_token: trialToken }
          : {
              trial_credentials:
                credentialTemplate === "key_pair"
                  ? { access_key: accessKey, secret_key: secretKey }
                  : { token: singleToken },
            };
      const result = await verifyMcp({ id: row.mcpId, body }).unwrap();
      if (!result.ok) {
        setError(result.message || result.error || "Verify failed");
        return;
      }
      setVerifiedOk(true);
      setTrialToken("");
      setAccessKey("");
      setSecretKey("");
      setSingleToken("");
      setStep(5);
    } catch (e) {
      setError(unwrapError(e));
    }
  };

  const handleCreateWithoutTry = async () => {
    setError(null);
    try {
      const row = await ensureCreated();
      setCreated(row);
      setSkipConfirm(false);
      setStep(5);
    } catch (e) {
      setError(unwrapError(e));
    }
  };

  const canNext = useMemo(() => {
    if (step === 1) return Boolean(name.trim());
    if (step === 2) return Boolean(serverUrl.trim().startsWith("https://"));
    if (step === 3) {
      if (authMode === "caller_token") return Boolean(payloadParameter.trim());
      return true;
    }
    return true;
  }, [step, name, serverUrl, authMode, payloadParameter]);

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Custom MCP"
    >
      <StepIndicator
        currentStep={step}
        totalSteps={5}
        label={STEP_LABELS[step]}
      />
      {error && <ErrorBanner>{error}</ErrorBanner>}

      {step === 1 && (
        <>
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="CareerVitae MCP"
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
          />
          <Text variant="body2">Type: Custom MCP (remote HTTP)</Text>
        </>
      )}

      {step === 2 && (
        <>
          <Input
            label="Server URL"
            value={serverUrl}
            onChange={(e) => setServerUrl(e.target.value)}
            placeholder="https://mcp.example.com/mcp"
          />
          <Text variant="body2">Auth mode</Text>
          <FilterChips
            items={[
              { id: "caller_token", label: "Caller token" },
              { id: "stored_credentials", label: "Stored credentials" },
            ]}
            activeId={authMode}
            onChange={(id) => setAuthMode(id as McpAuthMode)}
          />
        </>
      )}

      {step === 3 && authMode === "caller_token" && (
        <>
          <Text variant="body2">Send as</Text>
          <FilterChips
            items={[
              { id: "header", label: "HTTP header (recommended)" },
              { id: "body", label: "Request body" },
            ]}
            activeId={callerTokenIn}
            onChange={(id) => {
              const next = id as CallerTokenIn;
              setCallerTokenIn(next);
              if (next === "header" && !payloadParameter.startsWith("x-")) {
                setPayloadParameter("x-cv-mcp-token");
              }
            }}
          />
          <Input
            label="Parameter name"
            value={payloadParameter}
            onChange={(e) => setPayloadParameter(e.target.value)}
            placeholder={callerTokenIn === "header" ? "x-cv-mcp-token" : "cv_mcp_token"}
          />
          <Text variant="body2" style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
            {snippet(callerTokenIn, payloadParameter.trim() || "x-cv-mcp-token")}
          </Text>
        </>
      )}

      {step === 3 && authMode === "stored_credentials" && (
        <>
          <Text variant="body2">Template</Text>
          <FilterChips
            items={[
              { id: "key_pair", label: "Key pair" },
              { id: "single_token", label: "Single token" },
            ]}
            activeId={credentialTemplate}
            onChange={(id) => setCredentialTemplate(id as CredentialTemplate)}
          />
          <Text variant="body2">
            Values are pasted on the next step. GET never redisplays them.
          </Text>
        </>
      )}

      {step === 4 && (
        <>
          {authMode === "caller_token" ? (
            <Input
              label="Trial token"
              type="password"
              value={trialToken}
              onChange={(e) => setTrialToken(e.target.value)}
              placeholder="Paste a copied user token"
              autoComplete="off"
            />
          ) : credentialTemplate === "key_pair" ? (
            <>
              <Input
                label="Access key"
                type="password"
                value={accessKey}
                onChange={(e) => setAccessKey(e.target.value)}
                autoComplete="off"
              />
              <Input
                label="Secret key"
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                autoComplete="off"
              />
            </>
          ) : (
            <Input
              label="Token"
              type="password"
              value={singleToken}
              onChange={(e) => setSingleToken(e.target.value)}
              autoComplete="off"
            />
          )}
          <Text variant="body2">
            Caller-token paste is not stored. You can create without trying.
          </Text>
        </>
      )}

      {step === 5 && created && (
        <>
          <Text variant="h3">{created.name}</Text>
          <Text variant="body2">
            {verifiedOk
              ? "Verified. Associate this Custom MCP with an agent next."
              : "Created. Verify later from the instance page before associating agents."}
          </Text>
        </>
      )}

      <WizardFooter>
        <FooterActions>
          {step > 1 && step < 5 && (
            <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>
              Back
            </Button>
          )}
          {step < 4 && (
            <Button
              variant="primary"
              disabled={!canNext}
              onClick={() => setStep((s) => s + 1)}
            >
              Continue
            </Button>
          )}
          {step === 4 && (
            <>
              <Button variant="outline" disabled={busy} onClick={() => setSkipConfirm(true)}>
                Create without try
              </Button>
              <Button variant="primary" disabled={busy} onClick={handleTry}>
                Try connection
              </Button>
            </>
          )}
          {step === 5 && created && (
            <Button variant="primary" onClick={() => onOpenDetail(created)}>
              View Custom MCP
            </Button>
          )}
        </FooterActions>
      </WizardFooter>

      <Modal
        isOpen={skipConfirm}
        onClose={() => setSkipConfirm(false)}
        title="Create without trying?"
      >
        <Text variant="body1">
          Prefer Try before associating agents. Create anyway?
        </Text>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={() => setSkipConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={handleCreateWithoutTry}>
            Create
          </Button>
        </div>
      </Modal>
    </SlideOver>
  );
};

export default CustomMcpWizard;
